import React from "react";
import _ from "lodash";
import { getCloudUrl } from "Constants";
import { colors } from "@toddle-edu/ds-theme";
import {
  ADVISOR,
  ELEMENT_TYPE_DP_EXTENDED_ESSAY,
  ELEMENT_TYPE_DP_TOK,
  SUPERVISORS,
  TERM_WISE,
  SEPARATELY_FOR_EACH_TERM,
  NUMERIC_VALUE,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import moment from "moment";
import AnimateHeight from "UIComponents/AnimateHeight";
import {
  getLanguageNameByCode,
  getNationality,
} from "@toddle-edu/common-utils";
import { generateRandomId } from "Utils/GenerateRandomId";
import { getGenderOptions, getPronounsLabel } from "Utils";
import { getAcademicYearDetailsFromCahce } from "modules/CommonGraphqlHelpers";
import {
  SUBJECT_DISCIPLINE,
  COURSE_DISCIPLINE,
} from "ProgressReportCommon/modules/stringConstants";
import { LEVEL_NODE_UIDS, pypElementMapping } from "./constants";
import { getValueBasedOnReportType } from "./reportTypeHelpers";
import {
  getIsMYPCriteriaTotalAsAggregateEnabled,
  getIsProjectGradebookInReportsEnabled,
} from "./featureFlags";
import { getLevelToCriteriaMap } from "./criteriaHelpers";

const DISCIPLINE_NODE_UIDS = [SUBJECT_DISCIPLINE, COURSE_DISCIPLINE];

const getUpdatedAddressLine = ({ line, nextLine = null }) => {
  if (line) {
    const trimmedLine = _.trimEnd(line);

    if (!_.endsWith(trimmedLine, ",") && nextLine) {
      return trimmedLine + "," + " ";
    }

    return line + " ";
  }

  return "";
};

const getTotalSelectedStudents = studentProgressReport => {
  return _.filter(studentProgressReport, item => {
    const status = _.get(item, "status", "NA");
    return status != "NA";
  });
};

const getTotalSelectedStudentIds = studentProgressReport => {
  return _.map(getTotalSelectedStudents(studentProgressReport), item => {
    return _.get(item, "student.id", "");
  });
};

export const getDynamicLevelComponent = () => [
  ...(!getValueBasedOnReportType({
    defaultValue: false,
  })
    ? []
    : ["atl"]),
  "dpAtl",
  "mypAtl",
  "ubdAtl",
  "ubdLearningStandard",
  "mypLearningStandard",
  "dpSubjectStandard",
  "benchmark",
];

export const getProjectHeadLabel = ({ projectHead, t, projectHeadCount }) => {
  let projectHeadLabel = "";
  switch (projectHead) {
    case SUPERVISORS:
      projectHeadLabel =
        projectHeadCount > 1
          ? t("project:supervisor_plural")
          : t("progressReport:supervisor_name");
      break;
    case ADVISOR:
      projectHeadLabel = t("progressReport:adviser_name");
      break;
  }
  return projectHeadLabel;
};

export const getFieldsForProjectDetails = ({ elementType, t }) => {
  const projectFieldMapping = {
    [ELEMENT_TYPE_DP_EXTENDED_ESSAY]: [
      { label: t("common:title"), id: "title" },
      {
        label: t("progressReport:research_question"),
        id: "researchQuestion",
      },
    ],

    [ELEMENT_TYPE_DP_TOK]: [
      {
        label: t("progressReport:exhibition_ia_prompt"),
        id: "IAPrompt",
      },
      {
        label: t("common:title_of_the_with_label", {
          label: t("common:essay"),
        }),
        id: "essayTitle",
      },
    ],
  };
  return _.get(projectFieldMapping, [elementType], []);
};

export const checkIsNumericValueCalculationMethod = ({ criteriaSet }) => {
  const { calculateStandardGradesUsing } = criteriaSet || {};

  return calculateStandardGradesUsing === NUMERIC_VALUE;
};

// getCustomAcademicCriteriaValues moved to ratingPrimitives.js
export { getCustomAcademicCriteriaValues } from "./ratingPrimitives";

export const getUnitPlanReorderStepData = ({ unitPlans }) => {
  const nodes = {};
  const rootNodes = [];

  _.forEach(unitPlans, item => {
    const { id, title, theme } = item;
    rootNodes.push(item.id);
    nodes[id] = {
      id,
      label: _.isString(title) ? title : _.get(title, "value", ""),
      parent: null,
      children: [],
      subLabel: _.get(theme, "resolvedMinimalTree.themes.0.label", ""),
      unitPlan: item,
    };
  });

  return { nodes, rootNodes };
};

export const getProgressReportTaskSteps = ({
  studentProgressReport,
  unitPlans,
  modalMode,
  attendanceDates,
  signatories,
  teacherSignatoriesStatus,
}) => {
  const steps = [];
  if (modalMode == "ALL" || modalMode == "EDIT_UNIT_PLANS") {
    steps.push({
      id: "up",
      data: _.map(unitPlans, item => {
        const { id, title, image, unitType, subjects, theme } = item;
        return {
          id,
          title: _.isString(title) ? title : _.get(title, "value", ""),
          image,
          unitType,
          subjects,
          theme,
        };
      }),
    });
  }
  if (modalMode == "ALL" || modalMode == "REORDER_UNIT_PLANS") {
    const unitPlanReorderData = getUnitPlanReorderStepData({ unitPlans });
    steps.push({ id: "unitReOrder", data: unitPlanReorderData });
  }
  if (modalMode == "ALL" || modalMode == "EDIT_STUDENTS") {
    steps.push({
      id: "stu",
      data: getTotalSelectedStudentIds(studentProgressReport),
    });
  }
  if (modalMode == "EDIT_ATTENDANCE") {
    steps.push({
      id: "attendance",
      data: attendanceDates,
    });
  }
  if (
    (modalMode == "ALL" && teacherSignatoriesStatus) ||
    modalMode == "EDIT_SIGNATORIES"
  ) {
    steps.push({
      id: "signatories",
      data: signatories,
    });
  }
  return steps;
};

const getSignatoriesGroup = ({ newSignatories, oldSignatories }) => {
  // 1. added signatories
  const addedSignatories = _.differenceBy(newSignatories, oldSignatories, "id");
  const added = _.reduce(
    addedSignatories,
    (result, signatory) => {
      const { name, designation, signatureImage, displaySequence } = signatory;
      result.push({ name, designation, signatureImage, displaySequence });
      return result;
    },
    []
  );

  // 2. removed signatories
  const removedSignatories = [
    ..._.differenceBy(oldSignatories, newSignatories, "id"),
  ];

  const removed = _.map(removedSignatories, signatory =>
    _.get(signatory, "id")
  );

  // 3. updated signatories
  const updatedSignatories = _.intersectionWith(
    newSignatories,
    oldSignatories,
    (val1, val2) => {
      if (_.includes(removed, _.get(val2, "id"))) {
        return false;
      }
      return _.get(val1, "id") === _.get(val2, "id") && !_.isEqual(val1, val2);
    }
  );

  const updated = _.map(updatedSignatories, signatory =>
    _.pick(signatory, [
      "id",
      "name",
      "designation",
      "signatureImage",
      "displaySequence",
    ])
  );

  return { added, removed, updated };
};

export const getProgressReportTaskModalUpdatedValues = ({
  studentProgressReport,
  params,
  modalMode,
  signatories,
  teacherSignatoriesStatus,
}) => {
  const selectedUnitplans = params["up"];
  const selectedStudents = params["stu"];
  const selectedAttendanceDates = params["attendance"];
  const unitPlanReorderData = _.get(
    params["unitReOrder"],
    "rootNodes",
    selectedUnitplans
  );

  const oldStudentProgressReport = getTotalSelectedStudentIds(
    studentProgressReport
  );
  const newSignatories = params["signatories"];
  const oldSignatories = signatories;
  let unitPlanIds = undefined;
  let addedStudents = undefined;
  let removedStudents = undefined;
  let attendanceDates = undefined;
  let signatoriesGroup = undefined;
  if (
    modalMode == "ALL" ||
    modalMode == "EDIT_UNIT_PLANS" ||
    modalMode == "REORDER_UNIT_PLANS"
  ) {
    unitPlanIds = unitPlanReorderData;
  }
  if (modalMode == "ALL" || modalMode == "EDIT_STUDENTS") {
    addedStudents = _.difference(selectedStudents, oldStudentProgressReport);
    removedStudents = _.difference(oldStudentProgressReport, selectedStudents);
  }

  if (modalMode == "ALL" || modalMode == "EDIT_ATTENDANCE") {
    attendanceDates = selectedAttendanceDates;
  }
  if (
    (modalMode == "ALL" && teacherSignatoriesStatus) ||
    modalMode == "EDIT_SIGNATORIES"
  ) {
    signatoriesGroup = getSignatoriesGroup({ newSignatories, oldSignatories });
  }

  return {
    addedStudents,
    removedStudents,
    // addedUnitPlanIds,
    // removedUnitPlanIds,
    attendanceDates,
    unitPlanIds,
    signatoriesGroup,
  };
};

const isValidUrl = ({ urlString }) => {
  try {
    return Boolean(new URL(urlString));
  } catch {
    return false;
  }
};

export const getValidCloudUrl = ({ link }) => {
  if (!link) return null;

  if (isValidUrl({ urlString: link })) {
    return link;
  } else {
    return `${getCloudUrl()}/${link}`;
  }
};

/**
 * This function checks for psNode (which stores progress summary configs)
 * and overrides the node properties.
 */

export const getProgressReportCardImageStyle = {
  0: {
    backgroundColor: colors.purple900,
    arrowStrokeColor: colors.purple500,
  },
  1: {
    backgroundColor: colors.violet900,
    arrowStrokeColor: colors.violet500,
  },
  2: {
    backgroundColor: colors.teal900,
    arrowStrokeColor: colors.teal500,
  },
  3: {
    backgroundColor: colors.yellow900,
    arrowStrokeColor: colors.yellow500,
  },
  4: {
    backgroundColor: colors.pink900,
    arrowStrokeColor: colors.pink500,
  },
};

//to calculate final grade

const getImageSignatureStyle = ({ position, index, length }) => {
  if (position === 0) return { alignSelf: "flex-end" };
  if (position === 1) return { alignSelf: "flex-start" };
  if (position === 2) {
    return index + 1 == length
      ? { alignSelf: "flex-end" }
      : { alignSelf: "center" };
  }
  return undefined;
};

const getAutoTextSignatureStyle = ({ position, index, length }) => {
  if (position === 0) return { alignSelf: "flex-end", textAlign: "right" };
  if (position === 1) return { alignSelf: "flex-start", textAlign: "left" };
  if (position === 2) {
    return index + 1 == length
      ? { alignSelf: "flex-end", textAlign: "right" }
      : { alignSelf: "center", textAlign: "center" };
  }
  return undefined;
};

export const getSignatureStyle = ({
  index,
  length,
  type,
  horizontalAlignment = "auto",
}) => {
  const modulo = length > 2 ? 3 : 2;
  const position = (index + 1) % modulo;

  switch (type) {
    case "image":
      return getImageSignatureStyle({ position, index, length });
    case "text": {
      switch (horizontalAlignment) {
        case "auto":
          return getAutoTextSignatureStyle({ position, index, length });
        case "left":
          return { alignSelf: "flex-start", textAlign: "left" };
        case "right":
          return { alignSelf: "flex-end", textAlign: "right" };
        case "center":
          return { alignSelf: "center", textAlign: "center" };
      }
      break;
    }
    default: {
      if (position === 0) return { alignItems: "flex-end", textAlign: "end" };
      if (position === 1)
        return { alignItems: "flex-start", textAlign: "start" };
      if (position === 2) return { alignItems: "center", textAlign: "center" };
      break;
    }
  }
};

//this function is used to render components of static pages outside of static pages
//currently this function is used to render signatory related components in Transcripts module.

export const renderStaticFieldComp = ({
  key,
  nodes,
  replicaId,
  data: propData,
  updateRefs,
  scale,
}) => {
  const data = _.get(propData, replicaId ? replicaId : key, {});
  const node = _.get(nodes, key, {});

  const customValueFunction = _.get(data, "customValueFunction", null);

  const Component = _.get(data, "component", null);
  const nodeValue = _.isEmpty(node.value) ? data.defaultValue : node.value;
  const value = customValueFunction ? customValueFunction(nodes) : nodeValue;

  if (Component) {
    return (
      <AnimateHeight
        duration={300}
        key={key}
        height={node.isEnabled ? "auto" : 0}
        style={{ width: "auto" }}
        customRef={ref => (updateRefs ? updateRefs({ ref, id: key }) : null)}
      >
        <Component
          {..._.omit(data, ["component"])}
          containerStyle={
            typeof data.containerStyle == "function"
              ? data.containerStyle(scale)
              : data.containerStyle
          }
          emptyStyle={
            _.isEmpty(node.value) && data.emptyStyle ? data.emptyStyle : {}
          }
          id={node.id}
          scale={scale}
          value={value}
          imageStyle={data.imageStyle ? data.imageStyle : {}}
        />
      </AnimateHeight>
    );
  }
  return null;
};

//This function adds dummy node (if required) to solve border issue in student details section in student transcript

export const addDummyNodeInTranscriptStudentDetails = ({
  nodes,
  replicationFormats,
}) => {
  const newNodes = _.cloneDeep(nodes);
  const studentDetailsEnabledChildren = _.filter(
    _.get(nodes, "studentDetails.children", []),
    nodeId => {
      const node = nodes[nodeId];
      return _.get(node, "isEnabled", false);
    }
  );
  //we need to create dummy children nodes in order to have border that has full width
  const newNodesCount = _.size(studentDetailsEnabledChildren) % 2;
  if (newNodesCount == 1) {
    const referenceNode = _.get(replicationFormats, "studentDetail", {});
    //clone the reference  node
    const newNode = _.cloneDeep(referenceNode);
    newNode.id = generateRandomId();
    //finally add the new node in nodes
    newNodes[newNode.id] = newNode;
    newNodes.studentDetails.children = [
      ...newNodes.studentDetails.children,
      newNode.id,
    ];
  }
  return newNodes;
};

export const updateMypAssessmentCriteria = async ({
  data,
  academicTermId,
  gradeId,
  updateTermWisePhaseData,
  studentProgressReportId,
  filterNodeIds,
  reportId,
  reportFeedFilters,
  shouldRefetchSetFeed = false,
  isSubjectObjectivesSelectedFilter,
  setId,
  objectivesRefetchVariables,
  curriculumProgramId,
  subjects,
}) => {
  const { selectedObjectiveItems, previousSelectedObjectives } = data;

  const addedSubjectObjectives = [];

  _.forEach(selectedObjectiveItems, (value, key) => {
    const newObject = value;
    const oldObject = previousSelectedObjectives[key];
    const subject = _.find(subjects, { id: _.get(newObject, "subjectId", "") });
    const classGradeId = _.get(subject, "gradeId", "");
    if (
      _.get(newObject, "objectiveId", "") != _.get(oldObject, "objectiveId", "")
    ) {
      addedSubjectObjectives.push({
        criteriaId: _.get(newObject, "objectiveId", ""),
        studentId: _.get(newObject, "studentId", ""),
        subjectId: _.get(newObject, "subjectId", ""),
        academicTermId,
        gradeId: getValueBasedOnReportType({
          subjectValue: classGradeId,
          defaultValue: gradeId,
        }),
      });
    }
  });
  return await updateTermWisePhaseData({
    termGradeStudentWiseCriteriaInput: addedSubjectObjectives,
    studentProgressReportId,
    filterNodeIds,
    reportId,
    reportFeedFilters,
    isSubjectObjectivesSelectedFilter,
    ...(shouldRefetchSetFeed ? { setId } : {}),
    objectivesRefetchVariables,
    curriculumProgramId,
  });
};

//returns the node based on uid value sent

export const getShareReportTo = ({
  canShareWithFamily,
  canShareWithStudent,
}) => {
  if (canShareWithFamily && canShareWithStudent) {
    return "everyone";
  } else if (canShareWithFamily) {
    return "family";
  } else if (canShareWithStudent) {
    return "student";
  }
};

export const getStudentInfoValue = ({ node, t }) => {
  switch (node?.id) {
    case "address": {
      let addressValue = node?.value;
      if (_.isObject(addressValue)) {
        const { line1 = "", line2 = "", city = "" } = addressValue;

        addressValue = _.trim(
          `${getUpdatedAddressLine({
            line: line1,
            nextLine: `${line2}${city}`,
          })}${getUpdatedAddressLine({ line: line2, nextLine: city })}${
            city || ""
          }`
        );
      }

      return addressValue || "-";
    }
    case "dateOfBirth":
    case "prCreationDate": {
      const dateFormat = ACLStore.can(
        "FeatureFlag:ShowFullMonthInReportsTranscriptsDate"
      )
        ? "DD MMMM YYYY"
        : "DD MMM YYYY";
      const date = moment(node?.value).format(dateFormat);
      return !_.isEmpty(node?.value) ? date : "-";
    }
    case "gender": {
      const gender = _.get(
        _.find(getGenderOptions(t), item => item.value == node?.value),
        "label",
        ""
      );
      return gender || "-";
    }
    case "nationality":
    case "secondNationality":
    case "firstNationality": {
      const nationality = getNationality(node?.value, t);
      return nationality || "-";
    }
    case "firstLanguage":
    case "secondLanguage": {
      return getLanguageNameByCode(node?.value) || "-";
    }
    case "pronouns": {
      return getPronounsLabel({ pronouns: node?.value, t }) || "-";
    }
    default: {
      return node?.value || "-";
    }
  }
};

export const getAvatarSizeOnScale = ({ scale }) => {
  if (scale > 2) {
    return "x-large";
  } else if (scale > 1.5) {
    return "medium";
  } else {
    return "small";
  }
};

export const getAcademicYearFromId = ({ academicYearId }) => {
  const academicYear = getAcademicYearDetailsFromCahce(academicYearId);
  return academicYear;
};

export const updateDisplaySequence = nodes => {
  const updatedNodes = _.map(nodes, (node, idx) => {
    return { ...node, displaySequence: idx };
  });
  return updatedNodes;
};

export const getChildrenToLevelCriteriaMap = ({
  children,
  nodes,
  pypType,
  ratingLevels,
  parentNodeItemId,
  criteriaSet,
}) =>
  _.reduce(
    children,
    (acc, nodeId) => {
      const childNode = nodes[nodeId] || {};
      const { levelId, levels = [], itemId, itemName, label } = childNode;
      acc[nodeId] = getLevelToCriteriaMap({
        levels: [{ id: levelId, value: itemName || label }, ...levels],
        parentId:
          childNode?.uid == "PYP_LEARNING_STANDARD" ? parentNodeItemId : itemId,
        type: pypElementMapping[pypType],
        ratingLevels,
        criteriaSet,
      });
      return acc;
    },
    {}
  );

export const getShowTermsColumn = ({
  levelToCriteriaMap,
  isNewGradingSetup,
}) => {
  return (
    isNewGradingSetup || _.some(levelToCriteriaMap, value => value.length > 0)
  );
};

export const getShowTermsColumnForDynamicTable = ({
  childrenToLevelCriteriaMap,
  isNewGradingSetup,
}) => {
  return (
    isNewGradingSetup ||
    _.some(childrenToLevelCriteriaMap, value =>
      getShowTermsColumn({ levelToCriteriaMap: value })
    )
  );
};

// Check if current subject, criteria set for a particular term is not included in score aggregation

export const getFilteredAcademicTerms = ({
  academicTerms,
  showAttendanceFor,
}) =>
  _.filter(academicTerms, ({ isDerived }) => {
    switch (showAttendanceFor) {
      case SEPARATELY_FOR_EACH_TERM:
        return true;
      case TERM_WISE:
        return !isDerived;
      default:
        return isDerived;
    }
  });

export const getNumberOfHeaderColumns = ({
  showTermsColumn,
  sizeOfTerms,
  showCriteriaColumn,
}) => {
  let numberOfHeaderColumns = 1;
  if (showTermsColumn) {
    numberOfHeaderColumns += sizeOfTerms;
  }
  if (showCriteriaColumn) {
    numberOfHeaderColumns += 1;
  }
  return numberOfHeaderColumns;
};

export const getLabelColSpan = ({
  criteriaType,
  criteria,
  isMultiCriteriaTable,
  termIds,
  isFromTemplateEnd = false,
  criteriaLabelColSpan,
  increaseColSpanBy = 0,
  isNewGradeScaleSettingsEnabled = false,
}) => {
  switch (criteriaType) {
    case "MYP_CRITERIA_TOTAL":
    case "MYP_CRITERIA_FINAL_SCORE": {
      return isNewGradeScaleSettingsEnabled && !isMultiCriteriaTable
        ? criteriaLabelColSpan
        : 1 + criteriaLabelColSpan;
    }
    default: {
      return _.isEmpty(criteria) && !isMultiCriteriaTable && !isFromTemplateEnd
        ? _.size(termIds) + 1 + increaseColSpanBy
        : 1;
    }
  }
};

export const getShowCriterionLabel = ({
  criteriaType,
  isCompactUi,
  nodeUid,
}) => {
  switch (criteriaType) {
    case "MYP_CRITERIA_TOTAL":
    case "MYP_CRITERIA_FINAL_SCORE": {
      const isMYPCriteriaTotalAsAggregateEnabled = getIsMYPCriteriaTotalAsAggregateEnabled();

      return (
        isCompactUi ||
        (nodeUid == "PROGRESS_SUMMARY_SUBJECTS" &&
          isMYPCriteriaTotalAsAggregateEnabled)
      );
    }
    default: {
      return true;
    }
  }
};

export const getShowIbObjectiveTab = () =>
  ACLStore.can("FeatureFlag:ShowIbObjectiveTab");

//Get updated grade node ID for based on criteria type. For MYP Criterion we need to pass criterion nodes for rest other we need to
// pass subject grade node

export const getProjectIdNodeUID = ({ uid, parent = "" }) => {
  const isProjectGradebookInReportsEnabled = getIsProjectGradebookInReportsEnabled();
  if (!isProjectGradebookInReportsEnabled) return "";
  switch (uid) {
    case "PP_SUPERVISOR_COMMENT":
    case "PP_CRITERION":
    case "PP_DESCRIPTION":
      return "PP_DETAILS";
    case "CP_SUPERVISOR_COMMENT":
    case "CP_CRITERION":
    case "CP_DESCRIPTION":
      return "CP_DETAILS";
    case "SA_DESCRIPTION":
    case "SA_SUPERVISOR_COMMENT":
      return "SA_ACTIVITY";
    case "PROGRESS_SUMMARY_MYP_PROJECTS_COMMENT": {
      const mypProjectParentMap = { pp: "PP_DETAILS", sa: "SA_ACTIVITY" };
      return mypProjectParentMap[parent] || "CP_DETAILS";
    }
    case "CAS_DESCRIPTION":
    case "DP_CAS_COMMENT":
      return "CAS_PROJECT";
    case "EE_DESCRIPTION":
    case "DP_EE_COMMENT":
    case "EE_CRITERION":
      return "EE_DETAILS";
    case "DP_TOK_ESSAY_DESCRIPTION":
    case "DP_TOK_ESSAY_SUPERVISOR_COMMENT":
      return "DP_TOK_ESSAY_DETAILS";
    case "DP_TOK_EXHIBITION_DESCRIPTION":
    case "DP_TOK_EXHIBITION_SUPERVISOR_COMMENT":
      return "DP_TOK_EXHIBITION_DETAILS";
    case "PROGRESS_SUMMARY_DP_CORE_COMMENT": {
      const dpCoreParentMap = {
        cas: "CAS_PROJECT",
        ee: "EE_DETAILS",
        "tok=>essay": "DP_TOK_ESSAY_DETAILS",
        "tok=>exhibition": "DP_TOK_EXHIBITION_DETAILS",
      };
      return dpCoreParentMap[parent] || "";
    }
    case "SL_DESCRIPTION":
    case "SL_COMMENT":
      return "SL_PROJECT";
    case "RP_DESCRIPTION":
    case "RP_COMMENT":
    case "RP_CRITERION":
      return "RP_DETAILS";
    case "PROGRESS_SUMMARY_CP_CORE_COMMENT":
      return parent === "sl" ? "SL_PROJECT" : "RP_DETAILS";
    case "PP_CRITERIA":
    case "CP_CRITERIA":
    case "EE_ASSESSMENT_CRITERIA":
    case "RP_ASSESSMENT_CRITERIA":
    case "CAS_LEARNING_OUTCOME":
    case "SL_LEARNING_OUTCOME":
      return uid;
    default:
      return "";
  }
};

export const getUseBlackBordersForProgressReportTables = () =>
  ACLStore.can("FeatureFlag:UseBlackBordersForProgressReportTables");

export const getPypUnitNodeVisibility = ({
  nodes,
  showUnitConfigurationInReports,
  gradeScaleOrAcs,
}) => {
  // To show/hide all basic details for units
  const showBasicDetails = _.get(
    nodes,
    ["psuBasicDetails", "isEnabled"],
    !showUnitConfigurationInReports
  );
  const showCentralIdea = _.get(
    nodes,
    ["psuBasicDetails=>centralIdea", "isEnabled"],
    !showUnitConfigurationInReports
  );
  const showGradeSummary =
    _.get(
      nodes,
      ["psuBasicDetails=>gradeSummary", "isEnabled"],
      !showUnitConfigurationInReports
    ) && !_.isEmpty(gradeScaleOrAcs);

  const showCoverImage = _.get(
    nodes,
    ["psuBasicDetails=>coverImage", "isEnabled"],
    true
  );
  return {
    showBasicDetails,
    showCentralIdea,
    showGradeSummary,
    showCoverImage,
  };
};

// Create a map of default course nodes using UIDs for quick lookup (including nested children)

const TEACHER_COMMENT_UIDS = [
  "SUBJECT_TEACHER_COMMENT",
  "COURSE_TEACHER_COMMENT",
  "IL_TEACHER_COMMENT",
  "ADVISOR_COMMENT",
  "ADVISOR_TEACHER_COMMENT",
];

const SUPERVISOR_COMMENT_UIDS = [
  "CP_SUPERVISOR_COMMENT",
  "PP_SUPERVISOR_COMMENT",
  "SA_SUPERVISOR_COMMENT",
];

export const getCommentLabelForReport = ({
  node,
  showCommentLabel,
  t,
  nodes,
}) => {
  if (!showCommentLabel) {
    return "";
  }

  const isTeacherCommentNode = _.includes(
    TEACHER_COMMENT_UIDS,
    _.get(node, "uid", "")
  );

  const isSupervisorCommentNode = _.includes(
    SUPERVISOR_COMMENT_UIDS,
    _.get(node, "uid", "")
  );

  const isObjectivesComment =
    _.get(node, "pypType", "") === "mypObjectives" ||
    _.get(node, "pypType", "") === "dpCriteria";

  const isUnitReportSubjectComment = _.includes(
    ["UNIT_REPORT_SUBJECT"],
    _.get(node, "uid", "")
  );

  const isReportV2 = getValueBasedOnReportType();

  if (isReportV2 && isUnitReportSubjectComment) {
    const unitReportSubjectCommentNode = _.get(nodes, "psuSubjectComment", {});
    return _.get(unitReportSubjectCommentNode, "label", t("common:comment"));
  }

  if (
    isReportV2 &&
    !isTeacherCommentNode &&
    !isSupervisorCommentNode &&
    !isObjectivesComment &&
    !isUnitReportSubjectComment
  ) {
    return _.get(node, "label", t("common:comment"));
  }

  return _.get(node, "commentLabel", t("common:comment"));
};

export const getShowValue = ({
  academicCriteriaValues,
  isNumericGradeScale,
}) => {
  if (isNumericGradeScale) return false;
  const removeAbbreviationLimit = ACLStore.can(
    "FeatureFlag:RemoveAbbreviationLimit"
  );
  if (!removeAbbreviationLimit) {
    return true;
  }
  return _.reduce(
    academicCriteriaValues,
    (result, value) => {
      const { label = "", abbreviation = "" } = value;
      return result || label != abbreviation;
    },
    false
  );
};

export const getColumnStyle = ({ showLabel, showDescription, column }) => {
  switch (column) {
    case "abbreviation": {
      if (!showLabel && !showDescription) {
        return { width: "100%" };
      } else if (showLabel && !showDescription) {
        return { width: "80%" };
      }
      break;
    }
    case "description": {
      if (!showLabel && showDescription) {
        return { width: "80%" };
      }
      break;
    }
    default:
      return {};
  }
  return {};
};

export const calculateSignatureWidthInReports = ({ noOfSignatures, scale }) => {
  const minWidth = scale * 100;
  const maxWidthPercentage = 50;
  const columnGap = 40;

  // For single signature in current row, use maxWidth of 50%
  if (noOfSignatures === 1) {
    return {
      minWidth: `${minWidth}px`,
      maxWidth: `${maxWidthPercentage}%`,
      width: `calc(50% - ${columnGap / 2}px)`,
    };
  }

  // For 2 signatures, each takes up to 50% minus gap
  if (noOfSignatures === 2) {
    return {
      minWidth: `${minWidth}px`,
      width: `calc(50% - ${columnGap / 2}px)`,
      maxWidth: `calc(50% - ${columnGap / 2}px)`,
    };
  }

  // For 3 or more than 3 signatures
  if (noOfSignatures >= 3) {
    const widthPercentage = 100 / 3;
    const gapAdjustment = (columnGap * 2) / 3;

    return {
      minWidth: `${minWidth}px`,
      width: `calc(${widthPercentage}% - ${gapAdjustment}px)`,
      maxWidth: `calc(${widthPercentage}% - ${gapAdjustment}px)`,
    };
  }
};

export const getShouldHideProjectCriteriaTotal = () => {
  return ACLStore.can("FeatureFlag:HideProjectsCriteriaTotal");
};

export const createRowStripeGenerator = ({
  startIndex = 0,
  evenValue = "even",
  oddValue = "odd",
} = {}) => {
  let index = startIndex;

  return {
    next() {
      const value = index % 2 === 0 ? evenValue : oddValue;
      index += 1;
      return value;
    },

    peek() {
      return index % 2 === 0 ? evenValue : oddValue;
    },

    reset(newStartIndex = 0) {
      index = newStartIndex;
    },

    getIndex() {
      return index;
    },
  };
};

export const getEnabledLevelAndDisciplineNodes = ({
  nodes,
  nodeChildren = [],
}) => {
  const levelNodeId = _.find(nodeChildren, childId => {
    const childUid = _.get(nodes, [childId, "uid"]);
    return _.includes(LEVEL_NODE_UIDS, childUid);
  });
  const levelNode = levelNodeId ? nodes[levelNodeId] : null;

  const disciplineNodeId = _.find(nodeChildren, childId => {
    const childUid = _.get(nodes, [childId, "uid"]);
    return _.includes(DISCIPLINE_NODE_UIDS, childUid);
  });
  const disciplineNode = disciplineNodeId ? nodes[disciplineNodeId] : null;

  const isLevelEnabled = _.get(levelNode, "isEnabled");
  const isDisciplineEnabled = _.get(disciplineNode, "isEnabled");

  return {
    levelNode,
    showLevel: !_.isEmpty(levelNode) && isLevelEnabled,
    disciplineNode,
    showDiscipline: !_.isEmpty(disciplineNode) && isDisciplineEnabled,
  };
};

const getSortedEntities = ({ entities = [] }) => {
  return _.orderBy(
    entities,
    [entity => _.toLower(_.get(entity, "label", ""))],
    ["asc"]
  );
};

export const getUpdatedEntities = ({ entities = [], shouldSort = false }) => {
  const updatedEntities = _.map(entities, entity => {
    const label =
      _.get(entity, "label", null) ||
      _.get(entity, "name", null) ||
      _.get(entity, "title", "");

    return {
      ...entity,
      label,
    };
  });

  return shouldSort
    ? getSortedEntities({ entities: updatedEntities })
    : updatedEntities;
};
